<template>
  <div
    ref="selectTagsWrapper"
    class="bitts-select-tags"
    :class="{ error }"
    @paste.capture="handlePaste"
    @keydown.capture="rememberDirection"
    @keydown="stepPastHiddenSelected"
  >
    <BittsFormLabel
      v-if="formLabel"
      :label="formLabel"
      :disabled="disabled"
      :for="selectTagsId"
    />
    <ASelect
      :virtual="false"
      ref="tagsInput"
      v-model:value="value"
      :open="isOpen"
      :id="selectTagsId"
      data-testid="bitts-select-tags"
      :allow-clear="allowClear"
      :autofocus="autoFocus"
      :disabled="disabled"
      :token-separators
      size="large"
      :mode
      :options
      :popup-class-name="
        (filteredOptions?.length ?? 0) > 0
          ? 'bitts-select-tags__dropdown' +
            (hideSelected ? ' ant-select-dropdown-hide-selected' : '')
          : 'hidden'
      "
      @change="handleChange"
      @search="handleSearch"
      @dropdown-visible-change="handleDropdownVisibleChange"
      :filter-option="filterOption"
      :get-popup-container="getPopupContainer"
    >
      <template #dropdownRender="{ menuNode }">
        <component :is="menuNode" />
        <BittsSelectOptionPositions
          :select-id="selectTagsId"
          :is-skipping-selected="hideSelected"
        />
      </template>
      <template #placeholder>
        <div class="bitts-select__placeholder">
          <div v-if="prefixIcon" class="flex items-center">
            <FontAwesomeIcon
              :icon="prefixIcon"
              :style="{ width: '16px', color: 'currentColor' }"
              class="mr-8"
            />
            {{ placeholder }}
          </div>
          <div v-else>
            {{ placeholder }}
          </div>
        </div>
      </template>
      <template #suffixIcon>
        <FontAwesomeIcon
          :icon="['fak', 'chevron-down']"
          class="text-neutral-accent"
          :style="{ width: '16px', color: 'currentColor' }"
        />
      </template>
      <template #removeIcon>
        <FontAwesomeIcon
          :icon="['fak', 'x-circle']"
          class="text-neutral-accent"
          :style="{ width: '16px', color: 'currentColor' }"
        />
      </template>
      <template #tagRender="props">
        <ATag class="ant-select-selection-item flex" v-bind="{ ...props }">
          <BittsAvatar
            v-if="showAvatar"
            class="mr-8"
            :org="{
              clearbit_domain: props.option.clearbit_domain,
              logo_url: props.option.logo_url,
            }"
            size="x-small"
          />
          {{ props.label }}
          <template #closeIcon>
            <FontAwesomeIcon
              :icon="['fak', 'x-circle']"
              class="text-neutral-accent"
              :style="{ color: 'currentColor' }"
            />
          </template>
        </ATag>
      </template>
      <template #option="props: DefaultOptionType">
        <div class="flex content-center">
          <BittsAvatar
            v-if="showAvatar"
            class="mr-8"
            :org="{
              clearbit_domain: props.clearbit_domain,
              logo_url: props.logo_url,
            }"
            size="small"
          />
          <div
            v-if="!isExistingOption(props.value)"
            class="flex justify-between text-neutral-500"
          >
            <div>+ Add "{{ props.label }}"</div>
            <div> Or hit Enter </div>
          </div>
          <div v-else>{{ props.label }}</div>
        </div>
      </template>
    </ASelect>
    <span class="sr-only" aria-live="polite" aria-atomic="true">
      {{ searchAnnouncement }}
    </span>
    <p v-if="!warningMessage && note" class="bitts-select-tags__note">
      {{ note }}
    </p>
    <BittsAlert
      v-if="warningMessage"
      :message="warningMessage"
      color="warning"
      class="mt-8 mb-16"
    />
  </div>
</template>

<!--
  - 'tags' mode supports auto-tokenization, but 'multiple' mode does not.
  - In 'tags' mode, selected options are always visible, whereas 'multiple' mode allows hiding them.
  - The issue arises when we need both auto-tokenization and the ability to hide selected options.
  - To achieve this, we use 'tags' mode and hide selected options with CSS.
  - However, this causes visual bugs due to the virtual scrolling feature of the Ant Design Select component.
  - To resolve these issues, we disable virtual scrolling by setting the 'virtual' prop to false.
-->
<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { refDebounced } from '@vueuse/core';
import { Select as ASelect, Tag as ATag } from 'ant-design-vue';
import type {
  DefaultOptionType,
  LabeledValue,
  SelectValue,
} from 'ant-design-vue/es/select';
import type { RawValueType } from 'ant-design-vue/es/vc-select/Select';
import { computed, nextTick, ref, useId, useTemplateRef } from 'vue';

import BittsAlert from '../BittsAlert/BittsAlert.vue';
import BittsAvatar from '../BittsAvatar/BittsAvatar.vue';
import BittsFormLabel from '../BittsFormLabel/BittsFormLabel.vue';
import BittsSelectOptionPositions from '../BittsSelect/BittsSelectOptionPositions.vue';
import { useComboboxInputAria } from '../composables/useComboboxInputAria';

const {
  allowClear = true,
  autoFocus = false,
  disabled = false,
  placeholder = 'Start typing...',
  error = false,
  prefixIcon,
  note,
  warningMessage,
  formLabel,
  mode = 'tags',
  options,
  showAvatar = false,
  tokenSeparators = [',', ' '],
  getPopupContainer,
  hideSelected = true,
  ariaLabel,
} = defineProps<{
  allowClear?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  prefixIcon?: string | Record<string, unknown> | unknown[];
  mode?: 'tags' | 'multiple';
  note?: string;
  showAvatar?: boolean;
  warningMessage?: string;
  modelValue?: SelectValue;
  options?: DefaultOptionType[];
  formLabel?: Record<string, unknown> | string;
  tokenSeparators?: string[];
  getPopupContainer?: () => HTMLElement;
  hideSelected?: boolean;
  /** Names the field when it has no `formLabel`. */
  ariaLabel?: string;
}>();

const emit = defineEmits<{
  (event: 'change' | 'update:modelValue', value: SelectValue): void;
  (event: 'search', value: string): void;
}>();

const tagsInput = useTemplateRef('tagsInput');
const selectTagsWrapper = useTemplateRef<HTMLElement>('selectTagsWrapper');
useComboboxInputAria(selectTagsWrapper, () => ({
  hasVisibleLabel: !!formLabel,
  ariaLabel,
  isInvalid: error && !disabled,
  componentName: 'BittsSelectTags',
}));

const value = defineModel<SelectValue>('modelValue');

const ARROW_KEY_CODES = { ArrowDown: 40, ArrowUp: 38 } as const;
type ArrowKeyType = keyof typeof ARROW_KEY_CODES;
let direction: ArrowKeyType = 'ArrowDown';
let isStepping = false;
let isCheckPending = false;
let steps = 0;

const isOpen = ref(false);

function pressArrow(input: HTMLElement, key: ArrowKeyType) {
  isStepping = true;
  input.dispatchEvent(
    new KeyboardEvent('keydown', {
      key,
      keyCode: ARROW_KEY_CODES[key],
      which: ARROW_KEY_CODES[key],
      bubbles: true,
    }),
  );
  isStepping = false;
}

/**
 * Ant keeps the active option from the last time the list was open and also
 * passes the opening arrow key on to it, so the list reopened past the option
 * left active. The opening key is kept from Ant and the active option is moved
 * back to the top: ArrowDown opens on the first option and ArrowUp on the last.
 */
async function openFromArrow(input: HTMLElement, key: ArrowKeyType) {
  isOpen.value = true;
  await nextTick();
  const activeIndex = Number(
    input.getAttribute('aria-activedescendant')?.split('_list_').at(-1),
  );
  const stepsToTop = Number.isInteger(activeIndex) ? activeIndex : 0;
  for (let step = 0; step < stepsToTop; step++) pressArrow(input, 'ArrowUp');
  if (key === 'ArrowUp') pressArrow(input, 'ArrowUp');
  stepPastHiddenSelected();
}

function rememberDirection(event: KeyboardEvent) {
  if (isStepping) return;
  steps = 0;
  if (!(event.key in ARROW_KEY_CODES)) return;
  direction = event.key as ArrowKeyType;
  if (isOpen.value || disabled || !(event.target instanceof HTMLElement)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  openFromArrow(event.target, direction);
}

/**
 * Selected tags are hidden from the dropdown, but Ant keeps them in its option
 * list (tags mode adds them back) and still makes them active, so VoiceOver
 * read invisible tags and Enter removed them. Once Ant has handled a key or
 * opened, step past a hidden selected tag in the direction of travel, at most
 * once per option.
 */
async function stepPastHiddenSelected() {
  if (!hideSelected || isCheckPending) return;
  isCheckPending = true;
  await nextTick();
  isCheckPending = false;
  const input = selectTagsWrapper.value?.querySelector<HTMLElement>(
    '.ant-select-selection-search-input',
  );
  const active = document.getElementById(
    input?.getAttribute('aria-activedescendant') ?? '',
  );
  if (!input || input.getAttribute('aria-expanded') !== 'true') return;
  if (active?.getAttribute('aria-selected') !== 'true') return;
  const selectedCount = Array.isArray(value.value) ? value.value.length : 1;
  if (steps > (options?.length ?? 0) + selectedCount) return;
  steps += 1;
  pressArrow(input, direction);
}

function handleDropdownVisibleChange(isNowOpen: boolean) {
  const wasOpen = isOpen.value;
  isOpen.value = isNowOpen;
  if (!isNowOpen) {
    searchValue.value = '';
    return;
  }
  if (wasOpen) return;
  direction = 'ArrowDown';
  steps = 0;
  stepPastHiddenSelected();
}

const selectTagsId = useId();

const filteredOptions = computed(() => {
  if (!hideSelected || !value.value) return options;
  const currentValue = value.value;

  return options?.filter((option) => {
    if (!option.value) return true;

    if (typeof currentValue === 'number' || typeof currentValue === 'string') {
      return currentValue !== option.value;
    }
    if (!Array.isArray(currentValue) && 'value' in currentValue) {
      return currentValue.value !== option.value;
    }

    if (Array.isArray(currentValue) && typeof currentValue[0] !== 'object') {
      return !(currentValue as RawValueType[]).includes(option.value);
    }

    if (
      Array.isArray(currentValue) &&
      'value' in (currentValue[0] as LabeledValue)
    ) {
      return !(currentValue as LabeledValue[]).some(
        (item) => item.value === option.value,
      );
    }

    return true;
  });
});

function handleChange(val: SelectValue) {
  searchValue.value = '';
  emit('change', val);
}

const searchValue = ref('');

function handleSearch(val: string) {
  searchValue.value = val;
  emit('search', val);
}

function isSelectedValue(optionValue: unknown) {
  const current = value.value;
  return Array.isArray(current)
    ? (current as unknown[]).includes(optionValue)
    : current === optionValue;
}

/**
 * While typing, Ant keeps the same first option active and only changes its
 * text, so VoiceOver read neither the "Add" option nor how many tags match.
 * Announced once typing pauses.
 */
const searchMessage = computed(() => {
  const search = searchValue.value.trim();
  if (!isOpen.value || !search) return '';
  const matchCount = (options ?? []).filter(
    (option) =>
      String(option.label ?? '')
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      !(hideSelected && isSelectedValue(option.value)),
  ).length;
  const matches =
    matchCount === 1
      ? '1 matching tag'
      : matchCount
        ? `${matchCount} matching tags`
        : 'No matching tags';
  const canAdd = mode === 'tags' && !isExistingOption(search);
  return canAdd ? `Add "${search}": press Enter. ${matches}.` : `${matches}.`;
});
const searchAnnouncement = refDebounced(searchMessage, 500);

function isExistingOption(newValue?: string | number | null): boolean {
  return options?.some((option) => option.value === newValue) ?? false;
}

function filterOption(input: string, option?: DefaultOptionType) {
  return (
    option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
    !isExistingOption(option?.value)
  );
}

function handlePaste(e: ClipboardEvent) {
  const text = e.clipboardData?.getData('text');
  if (!text) return;

  // only intercept real bulk pastes
  if (!text.includes(',') && !text.includes(' ') && !text.includes('\n')) {
    return;
  }

  e.preventDefault();

  const tokens = text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  emit('update:modelValue', tokens);
  emit('change', tokens);
}

defineExpose({ tagsInput });
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-select-tags__note {
  @apply text-sm text-neutral-text-weak mt-8;
}

.bitts-select-tags {
  .ant-select {
    font-family: inherit;
  }
  .ant-select-multiple.ant-select-lg .ant-select-selection-search {
    @apply ms-0;
  }
  .ant-select-multiple .ant-select-selection-item {
    @apply rounded-bts-lg border-none bg-neutral-background text-neutral-text h-24 leading-6;
    .ant-select-selection-item-content {
      @apply pr-4 text-neutral-text;
    }
  }
  .ant-select:not(.ant-select-disabled):not(.ant-select-customize-input):not(
      .ant-pagination-size-changer
    ):hover
    .ant-select-selector {
    @apply border-neutral-border-strong;
  }
  .anticon-close-circle {
    @apply relative bottom-3;
  }
  .ant-select {
    @apply text-base leading-6 w-full;
  }
  .ant-select-selection-selected-value {
    @apply text-neutral-text;
  }

  .ant-select-selection-search {
    @apply ms-0;
  }

  .ant-select-selection-overflow {
    @apply mx-8;
  }
  .ant-select-arrow .ant-select-arrow-icon {
    @apply transition-transform;
  }
  .ant-select-open .ant-select-arrow-icon {
    @apply rotate-180;
  }
  .ant-select-selector {
    @apply flex-nowrap max-h-[160px] overflow-y-auto;
    scrollbar-width: thin;
  }
  .ant-select-clear {
    @apply mr-8;
  }
  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    @apply rounded-lg border-neutral-border-strong transition-none shadow-component;
  }
  .ant-select-selection-placeholder {
    @apply text-neutral-text-placeholder ml-4;
  }
  .ant-select-disabled.ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply bg-neutral-background-disabled;
  }
  .ant-select-multiple.ant-select-lg .ant-select-selection-item {
    @apply h-24 leading-6;
  }
  .ant-select-selection--multiple .ant-select-selection-item-remove {
    @apply right-8;
  }
  &.error .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    box-shadow: none !important;
    border-right-width: 2px !important;
    @apply border-danger-border-focus border-2;
  }
}
.ant-select-dropdown-menu-item,
.ant-select-dropdown-menu-item-group-list
  > .ant-select-dropdown-menu-item.ant-select,
.ant-select-dropdown-menu-item-group-list > .ant-select-dropdown-menu-item {
  @apply text-neutral-text-strong py-5 px-8 rounded;
}

.ant-select-dropdown-menu-item-active:not(
    .ant-select-dropdown-menu-item-disabled
  ) {
}
.ant-select-dropdown-menu-item:hover:not(
    .ant-select-dropdown-menu-item-disabled
  ) {
  @apply bg-neutral-background;
}
.ant-select-selection:focus,
.ant-select-selection:active {
  box-shadow: 0 0 0 1px theme(colors.neutral.border-focus);
  @apply border-neutral-border-focus;
}

.ant-select-dropdown-menu-item-group-title {
  @apply p-0 h-auto;
}
.ant-select-dropdown-menu-item-group:not(:last-child) {
  @apply border-b border-b-neutral-border pb-4 mb-4;
}
.ant-select-dropdown-menu {
  @apply p-4;
}
.ant-select-dropdown-menu-item-group-list
  .ant-select-dropdown-menu-item:first-child:not(:last-child),
.ant-select-dropdown-menu-item-group:not(:last-child)
  .ant-select-dropdown-menu-item-group-list
  .ant-select-dropdown-menu-item:last-child {
  @apply rounded-4;
}

.bitts-select-tags__dropdown {
  @apply border border-neutral-border rounded-8;
  &.ant-select-dropdown--multiple
    .ant-select-dropdown-menu-item-selected
    .ant-select-selected-icon,
  &.ant-select-dropdown--multiple
    .ant-select-dropdown-menu-item-selected:hover
    .ant-select-selected-icon {
    @apply text-info-accent;
  }
  &.ant-select-dropdown--multiple
    .ant-select-dropdown-menu-item:hover
    .ant-select-selected-icon {
    @apply text-neutral-text-strong;
  }
  .ant-select-dropdown-menu-item-active {
    @apply bg-neutral-background;
  }
  .ant-select-dropdown-menu-item-selected {
    @apply bg-info-background-weak font-normal;
  }
}

.ant-select-dropdown-hide-selected .ant-select-item-option-selected {
  @apply hidden;
}

.ant-select-selection-item {
  @apply flex items-center;
}
.ant-tag-close-icon {
  @apply flex;
}
.ant-select-item-option-active {
  @apply bg-primary-background-weak!;
}

.bitts-select-tags {
  .ant-tag {
    @apply rounded-bts-lg!;
  }
}
</style>
